import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { api } from "../../lib/api";
import { Loader2, Wand2, Stethoscope, ShieldAlert, AlertTriangle, CheckCircle2, Send, Pill, Check, Activity, Baby, ClipboardList, ShieldCheck, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { bmi, correctedAgeMonths, evalVitals } from "../../lib/calc";
import { Nom004Badge } from "../Nom004Badge";
import ConsultRecorder from "./ConsultRecorder";
import PremiumUpsell from "../PremiumUpsell";
import { isPremium } from "../../lib/premium";
import { useAuth } from "../../context/AuthContext";

const empty = { weight: "", height: "", head_circ: "", temperature: "", heart_rate: "", resp_rate: "", spo2: "", blood_pressure: "", current_illness: "", physical_exam: "", diagnosis: "", treatment: "" };

const TEMPLATES = [
  { label: "Niño sano", current_illness: "Acude a control de niño sano, asintomático.", physical_exam: "Buen estado general, hidratado, activo, reactivo. Desarrollo psicomotor acorde a la edad. Exploración por aparatos y sistemas sin alteraciones." },
  { label: "IVRS", current_illness: "Rinorrea, tos y congestión nasal de evolución reciente, sin dificultad respiratoria.", physical_exam: "Faringe hiperémica, otoscopia normal, campos pulmonares bien ventilados sin agregados." },
  { label: "GEPI", current_illness: "Evacuaciones disminuidas de consistencia y/o vómito.", physical_exam: "Hidratado, tolera vía oral. Abdomen blando, peristalsis aumentada, sin irritación peritoneal." },
  { label: "Dermatitis", current_illness: "Lesiones en piel pruriginosas.", physical_exam: "Lesiones eritematosas, sin datos de infección agregada." },
];
const CIE10 = ["J00 - Rinofaringitis aguda", "J02.9 - Faringitis aguda", "J03.9 - Amigdalitis aguda", "H66.9 - Otitis media", "J20.9 - Bronquitis aguda", "J21.9 - Bronquiolitis", "J45.9 - Asma", "A09 - Gastroenteritis infecciosa", "R50.9 - Fiebre", "L20.9 - Dermatitis atópica", "B34.9 - Infección viral", "Z00.1 - Control de niño sano", "E66.9 - Obesidad", "E44 - Desnutrición", "D50.9 - Anemia ferropénica", "N39.0 - Infección de vías urinarias", "B01.9 - Varicela"];

const ST = {
  alarma: { c: "text-destructive bg-destructive/10", i: ShieldAlert, l: "Fuera de rango", pulse: true },
  vigilancia: { c: "text-amber-600 bg-amber-50", i: AlertTriangle, l: "Vigilancia", pulse: false },
  normal: { c: "text-brand-sageDark bg-brand-sage/15", i: CheckCircle2, l: "Normal", pulse: false },
};

function Field({ label, unit, type = "number", testid, value, onChange }) {
  return (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{label}{unit ? ` (${unit})` : ""}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-xl mt-1 bg-stone-50 border-transparent focus:bg-white" />
    </div>
  );
}

function Section({ icon: Icon, title, children, action }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-wide text-brand-sage flex items-center gap-1.5"><Icon size={14} /> {title}</p>
        {action}
      </div>
      {children}
    </div>
  );
}

function AlarmRow({ items, testid }) {
  if (!items.length) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid={testid}>
      {items.map((e) => {
        const st = ST[e.status] || ST.normal; const Icon = st.i;
        const label = e.label?.split(" ")[0] || e.key;
        const detail = e.percentile != null ? `P${Math.round(e.percentile)}` : `${e.value}${e.unit || ""}`;
        return (
          <motion.div key={e.metric || e.key} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            data-testid={`alarm-${e.metric || e.key}`} className={`rounded-xl px-3 py-2 ${st.c} ${st.pulse ? "animate-pulse-alarm" : ""}`}>
            <p className="text-[10px] font-bold uppercase opacity-80">{label}</p>
            <p className="text-xs font-bold flex items-center gap-1 mt-0.5"><Icon size={11} />{detail} · {st.l}</p>
          </motion.div>
        );
      })}
    </div>
  );
}

export default function NewConsultationForm({ patient, adult = false, consultations = [], onSaved, onSendToRx, finalizeSignal = 0, onFinalizeDone, studyNote = null }) {
  const { user } = useAuth();
  const [form, setForm] = useState(empty);
  const [nurseVitals, setNurseVitals] = useState(null);
  const [ante, setAnte] = useState({
    perinatal_history: patient.perinatal_history || "",
    family_history: patient.family_history || "",
    pathological_history: patient.pathological_history || "",
    non_pathological_history: patient.non_pathological_history || patient.personal_history || "",
  });
  const [consultationId, setConsultationId] = useState(null);
  const [autosaving, setAutosaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [cie10Loading, setCie10Loading] = useState(false);
  const [liveEvals, setLiveEvals] = useState([]);
  const [aiTx, setAiTx] = useState(null);
  const [loadingTx, setLoadingTx] = useState(false);
  const [selMeds, setSelMeds] = useState({});
  const [selRecs, setSelRecs] = useState({});
  const timer = useRef(null);
  const saveTimer = useRef(null);
  const anteTimer = useRef(null);
  const savingNow = useRef(false);
  const initRef = useRef(false);
  const anteInit = useRef(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyStructured = (p) => {
    setForm((f) => ({
      ...f,
      current_illness: p.padecimiento_actual ? (f.current_illness ? `${f.current_illness}\n${p.padecimiento_actual}` : p.padecimiento_actual) : f.current_illness,
      physical_exam: p.exploracion_fisica ? (f.physical_exam ? `${f.physical_exam}\n${p.exploracion_fisica}` : p.exploracion_fisica) : f.physical_exam,
    }));
    setAnte((a) => ({
      ...a,
      perinatal_history: p.antecedentes_perinatales ? (a.perinatal_history ? `${a.perinatal_history}\n${p.antecedentes_perinatales}` : p.antecedentes_perinatales) : a.perinatal_history,
      family_history: p.antecedentes_heredofamiliares ? (a.family_history ? `${a.family_history}\n${p.antecedentes_heredofamiliares}` : p.antecedentes_heredofamiliares) : a.family_history,
      pathological_history: p.antecedentes_personales_patologicos ? (a.pathological_history ? `${a.pathological_history}\n${p.antecedentes_personales_patologicos}` : p.antecedentes_personales_patologicos) : a.pathological_history,
      non_pathological_history: p.antecedentes_personales_no_patologicos ? (a.non_pathological_history ? `${a.non_pathological_history}\n${p.antecedentes_personales_no_patologicos}` : p.antecedentes_personales_no_patologicos) : a.non_pathological_history,
    }));
  };
  const ageM = correctedAgeMonths(patient.birth_date, patient.gestational_weeks);

  // Nota de laboratorio enviada desde la pestaña Laboratorios (1 toque) → se anexa a Exploración física
  const studyRef = useRef(0);
  useEffect(() => {
    if (studyNote && studyNote._ts && studyNote._ts !== studyRef.current) {
      studyRef.current = studyNote._ts;
      const t = (studyNote.text || "").trim();
      if (t) {
        setForm((f) => ({ ...f, physical_exam: f.physical_exam ? `${f.physical_exam}\n\n[Estudios/Laboratorios]\n${t}` : `[Estudios/Laboratorios]\n${t}` }));
        toast.success("Nota agregada a la Exploración física de la consulta");
      }
    }
  }, [studyNote]);
  const liveBmi = bmi(parseFloat(form.weight), parseFloat(form.height));
  const vitalEvals = evalVitals(ageM, { hr: form.heart_rate, rr: form.resp_rate, temp: form.temperature, spo2: form.spo2 });

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    const ip = (consultations || []).find((c) => c.status === "en_progreso");
    if (ip) {
      setConsultationId(ip.consultation_id);
      setForm({
        weight: ip.weight ?? "", height: ip.height ?? "", head_circ: ip.head_circ ?? "",
        temperature: ip.temperature ?? "", heart_rate: ip.heart_rate ?? "", resp_rate: ip.resp_rate ?? "",
        spo2: ip.spo2 ?? "", blood_pressure: ip.blood_pressure ?? "", current_illness: ip.current_illness ?? "", physical_exam: ip.physical_exam ?? "",
        diagnosis: ip.diagnosis ?? "", treatment: ip.treatment ?? "",
      });
    } else {
      // Autollenado de signos capturados por Enfermería (editable por el médico)
      api.get(`/patients/${patient.patient_id}/pending-vitals`).then((r) => {
        const v = r.data;
        if (v && Object.keys(v).length) {
          setForm((f) => ({
            ...f,
            weight: v.weight ?? f.weight, height: v.height ?? f.height, head_circ: v.head_circ ?? f.head_circ,
            temperature: v.temperature ?? f.temperature, heart_rate: v.heart_rate ?? f.heart_rate,
            resp_rate: v.resp_rate ?? f.resp_rate, spo2: v.spo2 ?? f.spo2, blood_pressure: v.blood_pressure ?? f.blood_pressure,
          }));
          setNurseVitals(v);
        }
      }).catch(() => {});
    }
  }, [consultations]);

  const buildPayload = (src) => {
    const f = src || form;
    const p = {};
    ["weight", "height", "head_circ", "temperature"].forEach((k) => { if (f[k]) p[k] = parseFloat(f[k]); });
    ["heart_rate", "resp_rate", "spo2"].forEach((k) => { if (f[k]) p[k] = parseInt(f[k]); });
    if (f.blood_pressure) p.blood_pressure = f.blood_pressure;
    ["current_illness", "physical_exam", "diagnosis", "treatment"].forEach((k) => { if (f[k]) p[k] = f[k]; });
    return p;
  };

  const autosave = async () => {
    if (savingNow.current) return;
    savingNow.current = true;
    setAutosaving(true);
    try {
      const payload = buildPayload();
      if (consultationId) {
        await api.put(`/consultations/${consultationId}`, payload);
      } else {
        const res = await api.post("/consultations", { patient_id: patient.patient_id, status: "en_progreso", ...payload });
        setConsultationId(res.data.consultation_id);
      }
      setSavedAt(Date.now());
    } catch (e) { /* reintenta en el próximo cambio */ }
    finally { savingNow.current = false; setAutosaving(false); }
  };

  useEffect(() => {
    if (!initRef.current) return;
    if (!Object.values(form).some(Boolean)) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(autosave, 1000);
    return () => clearTimeout(saveTimer.current);
  }, [form]); // eslint-disable-line

  useEffect(() => {
    if (!anteInit.current) { anteInit.current = true; return; }
    clearTimeout(anteTimer.current);
    anteTimer.current = setTimeout(() => {
      api.put(`/patients/${patient.patient_id}`, ante).catch(() => {});
    }, 1000);
    return () => clearTimeout(anteTimer.current);
  }, [ante]); // eslint-disable-line

  useEffect(() => {
    if (!form.weight && !form.height && !form.head_circ) { setLiveEvals([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const res = await api.post("/somatometry/evaluate", {
          sex: patient.sex, age_months: ageM,
          weight: form.weight ? parseFloat(form.weight) : null,
          height: form.height ? parseFloat(form.height) : null,
          head_circ: form.head_circ ? parseFloat(form.head_circ) : null,
        });
        setLiveEvals(res.data.evals || []);
      } catch (e) {}
    }, 500);
    return () => clearTimeout(timer.current);
  }, [form.weight, form.height, form.head_circ, patient.sex, ageM]);

  const suggestTx = async () => {
    const dx = form.diagnosis || form.current_illness;
    if (!dx) { toast.error("Escribe el padecimiento o diagnóstico"); return; }
    setLoadingTx(true);
    try {
      const res = await api.post("/ai/assist", { patient_id: patient.patient_id, type: "treatment", diagnosis: dx, context: form.physical_exam, weight: (!adult && form.weight) ? parseFloat(form.weight) : undefined });
      const p = res.data.parsed;
      if (!p) { toast.error("La IA no devolvió datos"); return; }
      setAiTx(p);
      if (!form.treatment && p.sugerencias?.length) setForm((f) => ({ ...f, treatment: p.sugerencias.map((s) => s.titulo).join("; ") }));
    } catch (e) { toast.error("Error de IA"); }
    finally { setLoadingTx(false); }
  };

  const sendToRx = () => {
    const meds = (aiTx?.medicamentos || []).filter((_, i) => selMeds[i]).map((m) => ({ name: m.nombre, presentation: m.presentacion || "", dose: m.dosis_receta || m.dosis, dose_detail: m.dosis, route: m.via, frequency: m.frecuencia, duration: m.duracion }));
    const recs = [
      ...(aiTx?.sugerencias || []).filter((_, i) => selRecs[`s${i}`]).map((s) => `${s.titulo}: ${s.detalle}`),
      ...(aiTx?.signos_alarma?.length && selRecs.alarmas ? [`Signos de alarma: ${aiTx.signos_alarma.join(", ")}`] : []),
    ];
    if (meds.length === 0 && recs.length === 0) { toast.error("Selecciona medicamentos o recomendaciones"); return; }
    onSendToRx?.({ items: meds.length ? meds : [{}], indications: recs.join("\n"), diagnosis: form.diagnosis || form.current_illness });
    toast.success("Enviado a la receta");
  };

  const consultObj = () => ({
    current_illness: form.current_illness, physical_exam: form.physical_exam,
    diagnosis: form.diagnosis, treatment: form.treatment,
    weight: form.weight, height: form.height, heart_rate: form.heart_rate,
    resp_rate: form.resp_rate, temperature: form.temperature, spo2: form.spo2, blood_pressure: form.blood_pressure,
  });

  const doFinalize = async (extra) => {
    setAuditResult(null);
    setFinalizing(true);
    clearTimeout(saveTimer.current);
    const src = { ...form, ...(extra || {}) };
    try {
      const payload = buildPayload(src);
      if (consultationId) {
        await api.put(`/consultations/${consultationId}`, { ...payload, status: "finalizada" });
      } else {
        await api.post("/consultations", { patient_id: patient.patient_id, status: "finalizada", ...payload });
      }
      await api.put(`/patients/${patient.patient_id}`, ante).catch(() => {});
      toast.success("Consulta finalizada");
      setForm(empty); setConsultationId(null); setLiveEvals([]); setAiTx(null); setSelMeds({}); setSelRecs({}); setSavedAt(null);
      onSaved?.();
    } catch (e) { toast.error("Error al finalizar"); }
    finally { setFinalizing(false); onFinalizeDone?.(); }
  };

  const startFinalize = async () => {
    if (!Object.values(form).some(Boolean)) { toast.error("Captura datos de la consulta antes de finalizar"); onFinalizeDone?.(); return; }
    // Auditoría IA opcional (Plan Pro): solo si el médico premium la activó en el Asistente IA.
    if (!isPremium(user) || localStorage.getItem("clinped_audit_on_finalize") !== "1") {
      await doFinalize();
      return;
    }
    setAuditing(true);
    try {
      const res = await api.post("/ai/assist", { patient_id: patient.patient_id, type: "audit", consultation: consultObj() });
      const a = res.data.parsed;
      const hasCorr = a?.correcciones && Object.values(a.correcciones).some((v) => v && v.trim());
      const hasObs = a?.observaciones && a.observaciones.length > 0;
      if (a && (a.cumple === false || hasCorr || hasObs)) {
        setAuditing(false);
        setAuditResult(a);
        onFinalizeDone?.();
        return;
      }
    } catch (e) { /* si el análisis falla, finalizamos igual */ }
    setAuditing(false);
    await doFinalize();
  };

  const acceptCorrections = () => {
    const corr = {};
    ["current_illness", "physical_exam", "diagnosis", "treatment"].forEach((k) => { if (auditResult?.correcciones?.[k]?.trim()) corr[k] = auditResult.correcciones[k]; });
    doFinalize(corr);
  };

  const dxList = form.diagnosis ? form.diagnosis.split("\n") : [""];
  const setDx = (i, v) => {
    const arr = form.diagnosis ? form.diagnosis.split("\n") : [""];
    arr[i] = v;
    set("diagnosis", arr.join("\n"));
  };
  const addDx = () => set("diagnosis", (form.diagnosis ? form.diagnosis + "\n" : ""));
  const removeDx = (i) => {
    const arr = form.diagnosis ? form.diagnosis.split("\n") : [""];
    arr.splice(i, 1);
    set("diagnosis", arr.join("\n"));
  };

  const mapCie10 = async (i = 0) => {
    const arr = form.diagnosis ? form.diagnosis.split("\n") : [""];
    const dx = (arr[i] || "").trim();
    if (!dx) { toast.error("Escribe el diagnóstico primero"); return; }
    setCie10Loading(i);
    try {
      const res = await api.post("/ai/assist", { patient_id: patient.patient_id, type: "cie10", diagnosis: dx });
      const p = res.data.parsed;
      if (p?.codigo) { arr[i] = `${p.codigo} - ${p.descripcion}`; set("diagnosis", arr.join("\n")); toast.success("CIE-10 asignado"); }
      else toast.error("No se encontró un CIE-10");
    } catch (e) { toast.error("Error de IA"); }
    finally { setCie10Loading(false); }
  };

  useEffect(() => {
    if (finalizeSignal > 0) startFinalize();
  }, [finalizeSignal]); // eslint-disable-line

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-outfit font-bold text-lg text-brand-ink">Historia clínica</h3>
          {!adult && patient.gestational_weeks < 37 && <p className="text-xs text-brand-terracotta font-semibold">Edad corregida: {ageM} meses (prematuro {patient.gestational_weeks} SDG)</p>}
        </div>
        {autosaving ? (
          <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="autosave-indicator"><Loader2 size={12} className="animate-spin" /> Guardando…</span>
        ) : savedAt ? (
          <span className="text-xs text-brand-sageDark flex items-center gap-1" data-testid="autosave-indicator"><Check size={12} /> Guardado automáticamente</span>
        ) : (
          <span className="text-xs text-muted-foreground" data-testid="autosave-indicator">Se guarda solo</span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 items-center" data-testid="consult-progress">
        {[
          ["Somatometría", form.weight && form.height],
          ["Signos vitales", form.heart_rate || form.resp_rate || form.temperature || form.spo2],
          ["Padecimiento", form.current_illness],
          ["Exploración", form.physical_exam],
          ["Diagnóstico", form.diagnosis],
          ["Tratamiento", form.treatment],
        ].map(([label, done]) => (
          <span key={label} className={`text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 transition-all ${done ? "bg-brand-sage/15 text-brand-sageDark" : "bg-stone-100 text-muted-foreground"}`}>
            {done ? <Check size={11} /> : <span className="w-2.5 h-2.5 rounded-full border border-current opacity-40" />} {label}
          </span>
        ))}
        <Nom004Badge consultation={{ ...form, reason: form.reason }} testid="consult-nom004" />
      </div>

      {isPremium(user)
        ? <ConsultRecorder patientId={patient.patient_id} onStructured={applyStructured} />
        : <PremiumUpsell feature="el dictado de la consulta" />}

      {!adult && (
        <Section icon={Baby} title="Antecedentes perinatales">
          <Textarea data-testid="input-perinatal" value={ante.perinatal_history} onChange={(e) => setAnte({ ...ante, perinatal_history: e.target.value })} placeholder="Embarazo, parto, peso al nacer, complicaciones…" rows={2} className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
        </Section>
      )}

      <Section icon={ClipboardList} title="Antecedentes heredofamiliares">
        <Textarea data-testid="input-family-history" value={ante.family_history} onChange={(e) => setAnte({ ...ante, family_history: e.target.value })} placeholder="Diabetes, hipertensión, cáncer, etc. en familiares directos…" rows={2} className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
      </Section>

      <Section icon={ClipboardList} title="Antecedentes personales patológicos">
        <Textarea data-testid="input-pathological" value={ante.pathological_history} onChange={(e) => setAnte({ ...ante, pathological_history: e.target.value })} placeholder="Enfermedades previas, cirugías, hospitalizaciones, alergias…" rows={2} className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
      </Section>

      <Section icon={ClipboardList} title="Antecedentes personales no patológicos">
        <Textarea data-testid="input-non-pathological" value={ante.non_pathological_history} onChange={(e) => setAnte({ ...ante, non_pathological_history: e.target.value })} placeholder="Alimentación, inmunizaciones, desarrollo, vivienda…" rows={2} className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
      </Section>

      <Section icon={ClipboardList} title="Padecimiento actual">
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="note-templates">
          {TEMPLATES.map((t) => (
            <button key={t.label} type="button" data-testid={`tpl-${t.label}`} onClick={() => setForm((f) => ({ ...f, current_illness: t.current_illness, physical_exam: t.physical_exam }))}
              className="text-xs font-semibold bg-brand-sage/15 text-brand-sageDark px-2.5 py-1 rounded-full hover:bg-brand-sage/25 transition-all">+ {t.label}</button>
          ))}
        </div>
        <Textarea data-testid="input-current-illness" value={form.current_illness} onChange={(e) => set("current_illness", e.target.value)} placeholder="Inicio, evolución y síntomas del padecimiento actual…" rows={2} className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
      </Section>

      <Section icon={Stethoscope} title="Exploración física">
        <Textarea data-testid="input-physical-exam" value={form.physical_exam} onChange={(e) => set("physical_exam", e.target.value)} placeholder="Hallazgos de la exploración por aparatos y sistemas…" rows={2} className="rounded-xl bg-stone-50 border-transparent focus:bg-white" />
      </Section>

      {nurseVitals && (
        <div data-testid="nurse-vitals-badge" className="flex items-center gap-2 bg-brand-sage/10 border border-brand-sage/20 text-brand-sageDark rounded-2xl px-4 py-2.5 text-sm font-semibold">
          <Activity size={16} /> Signos y somatometría cargados por Enfermería — revísalos y edítalos si es necesario.
        </div>
      )}

      <Section icon={Activity} title="Somatometría">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Peso" unit="kg" testid="input-weight" value={form.weight} onChange={(v) => set("weight", v)} />
          <Field label="Talla" unit="cm" testid="input-height" value={form.height} onChange={(v) => set("height", v)} />
          {!adult && <Field label="P. Cefálico" unit="cm" testid="input-headcirc" value={form.head_circ} onChange={(v) => set("head_circ", v)} />}
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">IMC</Label>
            <div className="mt-1 rounded-xl bg-brand-sage/10 px-3 py-2 text-sm font-bold text-brand-sageDark">{(adult || ageM >= 24) ? (liveBmi || "—") : "—"}</div>
          </div>
        </div>
        {!adult && <AlarmRow items={liveEvals} testid="live-alarms" />}
      </Section>

      <Section icon={Activity} title={adult ? "Signos vitales" : "Signos vitales (con alarma por edad)"}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="FC" unit="lpm" testid="input-fc" value={form.heart_rate} onChange={(v) => set("heart_rate", v)} />
          <Field label="FR" unit="rpm" testid="input-fr" value={form.resp_rate} onChange={(v) => set("resp_rate", v)} />
          <Field label="Temp." unit="°C" testid="input-temp" value={form.temperature} onChange={(v) => set("temperature", v)} />
          <Field label="SatO₂" unit="%" testid="input-spo2" value={form.spo2} onChange={(v) => set("spo2", v)} />
          <Field label="TA" unit="mmHg" type="text" testid="input-ta" value={form.blood_pressure} onChange={(v) => set("blood_pressure", v)} />
        </div>
        {!adult && <AlarmRow items={vitalEvals} testid="vitals-alarms" />}
      </Section>

      <div className="bg-stone-50 rounded-2xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-wide text-brand-ink">Diagnóstico y tratamiento</p>
          <div className="flex items-center gap-1.5">
            <button data-testid="ai-suggest-tx" onClick={suggestTx} disabled={loadingTx} className="flex items-center gap-1.5 text-xs font-bold text-white bg-brand-ink px-3 py-1.5 rounded-full hover:opacity-90 disabled:opacity-60">
              {loadingTx ? <Loader2 size={14} className="animate-spin" /> : <Stethoscope size={14} />} Sugerir Tx
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-bold uppercase tracking-wide text-brand-sage flex items-center gap-1.5"><Stethoscope size={13} /> Diagnóstico(s)</p>
          <button type="button" data-testid="add-diagnosis-btn" onClick={addDx}
            className="flex items-center gap-1 text-xs font-bold text-brand-sageDark bg-brand-sage/15 hover:bg-brand-sage/25 px-2.5 py-1 rounded-full transition-all">
            <Plus size={13} /> Agregar diagnóstico
          </button>
        </div>
        <div className="space-y-2" data-testid="diagnosis-list">
          {dxList.map((dx, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`diagnosis-row-${i}`}>
              <span className="text-xs font-bold text-brand-ink/40 w-4 shrink-0 text-center">{i + 1}</span>
              <Input data-testid={`input-diagnosis-${i}`} list="cie10-list" value={dx} onChange={(e) => setDx(i, e.target.value)}
                placeholder="Diagnóstico (escribe o elige CIE-10)" className="rounded-xl bg-white border-transparent flex-1" />
              <button type="button" data-testid={`cie10-ai-btn-${i}`} onClick={() => mapCie10(i)} disabled={cie10Loading === i}
                className="flex items-center gap-1.5 text-xs font-bold text-white bg-brand-sage hover:bg-brand-sageDark px-3 py-2 rounded-xl disabled:opacity-60 shrink-0">
                {cie10Loading === i ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} CIE-10
              </button>
              {dxList.length > 1 && (
                <button type="button" data-testid={`remove-diagnosis-${i}`} onClick={() => removeDx(i)}
                  className="p-2 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0" title="Quitar diagnóstico">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          ))}
        </div>
        <datalist id="cie10-list">{CIE10.map((c) => <option key={c} value={c} />)}</datalist>
        <Textarea data-testid="input-treatment" value={form.treatment} onChange={(e) => set("treatment", e.target.value)} placeholder="Tratamiento / plan" rows={2} className="rounded-xl bg-white border-transparent" />

        {aiTx && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="consult-ai-tx">
            {aiTx.medicamentos?.length > 0 && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide text-brand-sage mb-1.5 flex items-center gap-1"><Pill size={13} /> Medicamentos sugeridos (marca para receta)</p>
                {aiTx.medicamentos.map((m, i) => (
                  <label key={i} className="flex items-start gap-2 bg-white rounded-xl p-2.5 cursor-pointer mb-1.5">
                    <input type="checkbox" data-testid={`sel-med-${i}`} checked={!!selMeds[i]} onChange={(e) => setSelMeds({ ...selMeds, [i]: e.target.checked })} className="mt-1 accent-brand-terracotta" />
                    <span className="text-sm"><b className="text-brand-ink">{m.nombre}</b> <span className="text-muted-foreground">— {[m.dosis, m.via, m.frecuencia, m.duracion].filter(Boolean).join(" · ")}</span></span>
                  </label>
                ))}
              </div>
            )}
            {aiTx.sugerencias?.length > 0 && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide text-brand-sage mb-1.5">Recomendaciones (marca para receta)</p>
                {aiTx.sugerencias.map((s, i) => (
                  <label key={i} className="flex items-start gap-2 bg-white rounded-xl p-2.5 cursor-pointer mb-1.5">
                    <input type="checkbox" data-testid={`sel-rec-${i}`} checked={!!selRecs[`s${i}`]} onChange={(e) => setSelRecs({ ...selRecs, [`s${i}`]: e.target.checked })} className="mt-1 accent-brand-sage" />
                    <span className="text-sm"><b className="text-brand-ink">{s.titulo}</b> <span className="text-muted-foreground">{s.detalle}</span></span>
                  </label>
                ))}
              </div>
            )}
            <button data-testid="send-to-rx" onClick={sendToRx} className="flex items-center gap-2 text-sm font-bold text-brand-terracotta"><Send size={15} /> Enviar seleccionados a la receta</button>
          </motion.div>
        )}
      </div>

      <div className="rounded-2xl bg-brand-sage/10 px-4 py-3 text-center">
        <p className="text-xs text-brand-sageDark font-semibold">Todo se guarda automáticamente. Al pulsar <b>“Finalizar consulta”</b> (pestaña <b>Asistente IA</b>), la IA revisará la nota (NOM-004) antes de cerrar.</p>
      </div>

      <Dialog open={!!auditResult} onOpenChange={(o) => { if (!o) setAuditResult(null); }}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-brand-ink flex items-center gap-2"><ShieldCheck size={20} className="text-brand-sageDark" /> Revisión de la atención antes de cerrar</DialogTitle>
          </DialogHeader>
          {auditResult && (
            <div className="space-y-3">
              <div className={`rounded-xl px-3 py-2 text-sm font-semibold ${auditResult.cumple ? "bg-brand-sage/15 text-brand-sageDark" : "bg-amber-50 text-amber-700"}`}>
                {auditResult.cumple ? "✓ Atención adecuada" : "⚠ Hay observaciones por mejorar"}
                {auditResult.resumen && <span className="block font-normal text-xs mt-1 opacity-90">{auditResult.resumen}</span>}
              </div>
              {auditResult.observaciones?.length > 0 && (
                <ul className="space-y-1.5">
                  {auditResult.observaciones.map((o, i) => (
                    <li key={i} className="text-sm text-brand-ink/80 flex gap-2"><AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" /><span><b>{o.area}:</b> {o.detalle}</span></li>
                  ))}
                </ul>
              )}
              {auditResult.correcciones && Object.values(auditResult.correcciones).some((v) => v && v.trim()) && (
                <div className="bg-stone-50 rounded-xl p-3 space-y-1.5">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-brand-sage">Corrección sugerida</p>
                  {["current_illness", "physical_exam", "diagnosis", "treatment"].map((k) => auditResult.correcciones[k]?.trim() && (
                    <p key={k} className="text-sm text-brand-ink/80"><b>{{ current_illness: "Padecimiento", physical_exam: "EF", diagnosis: "Dx", treatment: "Tx" }[k]}:</b> {auditResult.correcciones[k]}</p>
                  ))}
                </div>
              )}
              <div className="flex flex-col gap-2 pt-1">
                {auditResult.correcciones && Object.values(auditResult.correcciones).some((v) => v && v.trim()) && (
                  <button data-testid="audit-accept" onClick={acceptCorrections} disabled={finalizing}
                    className="w-full flex items-center justify-center gap-2 bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-semibold py-2.5 rounded-xl transition-all disabled:opacity-60">
                    {finalizing ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} Aplicar correcciones y finalizar
                  </button>
                )}
                <div className="flex gap-2">
                  <button data-testid="audit-finalize-asis" onClick={() => doFinalize()} disabled={finalizing}
                    className="flex-1 bg-brand-ink text-white font-semibold py-2.5 rounded-xl text-sm disabled:opacity-60">Finalizar sin cambios</button>
                  <button data-testid="audit-keep-editing" onClick={() => setAuditResult(null)} disabled={finalizing}
                    className="flex-1 bg-white border border-stone-200 text-brand-ink font-semibold py-2.5 rounded-xl text-sm">Seguir editando</button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
